import createClient, { type Middleware } from 'openapi-fetch';
import { ApiFout, isProbleem } from './fouten.ts';
import type { paths } from './schema';

const veiligeMethodes = new Set(['GET', 'HEAD', 'OPTIONS']);

let csrfToken: string | null = null;
let bijNietAangemeld: (() => void) | null = null;

/**
 * Onthoudt het CSRF-token van de sessie. Elk wijzigend verzoek stuurt het mee in de header `X-CSRF-Token`.
 * @param token Het token uit `/auth/me`, of `null` na afmelden.
 */
export function zetCsrfToken(token: string | null): void {
  csrfToken = token;
}

/**
 * Registreert wat er moet gebeuren als de API meldt dat de sessie niet meer geldig is.
 * @param handler De functie die de sessie in de app beëindigt.
 */
export function bijVerlopenSessie(handler: (() => void) | null): void {
  bijNietAangemeld = handler;
}

const csrf: Middleware = {
  async onRequest({ request }) {
    if (csrfToken && !veiligeMethodes.has(request.method)) {
      request.headers.set('x-csrf-token', csrfToken);
    }
    return request;
  },
};

/**
 * Het adres van de API. Standaard hetzelfde adres als de frontend; in ontwikkeling stuurt Vite /api door naar
 * de backend. Een API op een ander adres kies je met VITE_API_URL (dan is CORS op de backend nodig).
 */
export const apiBasis: string = import.meta.env['VITE_API_URL'] ?? '';

export const api = createClient<paths>({
  baseUrl: apiBasis,
  credentials: 'include',
  // De globale fetch wordt pas bij elke aanroep opgezocht, zodat tests hem kunnen vervangen.
  fetch: (verzoek) => globalThis.fetch(verzoek),
});
api.use(csrf);

interface Antwoord<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/**
 * Pakt het resultaat van een aanroep uit, of gooit een `ApiFout` met de foutmelding van de API.
 * @param aanroep De aanroep via de client.
 * @returns De data van het antwoord.
 */
export async function uitvoeren<T>(aanroep: Promise<Antwoord<T>>): Promise<T> {
  let antwoord: Antwoord<T>;
  try {
    antwoord = await aanroep;
  } catch (fout) {
    throw fout instanceof TypeError ? new ApiFout(0, null) : fout;
  }
  const { data, error, response } = antwoord;
  if (!response.ok) {
    const apiFout = new ApiFout(response.status, isProbleem(error) ? error : null);
    if (apiFout.status === 401) {
      bijNietAangemeld?.();
    }
    throw apiFout;
  }
  return data as T;
}

/**
 * Maakt het adres van een bestand van de API, bijvoorbeeld om een cv te downloaden.
 * @param pad Het pad onder de API, zoals `/api/v1/cvs/{id}/bestand`.
 * @returns Het volledige adres.
 */
export function apiAdres(pad: string): string {
  return `${apiBasis}${pad}`;
}
